import { z } from 'zod';
import { countryCodeSchema, currencyCodeSchema, decimalInput, optionalText, paginationQuerySchema, patchOf, } from './common.js';
import { optionalEmailSchema } from './auth.js';
import { addressFieldsShape, taxIdentifierTypeSchema } from './business.js';
export const LINE_TYPES = ['goods', 'service'];
export const lineTypeSchema = z.enum(LINE_TYPES);
/** A supplier or buyer exactly as printed on an invoice (stored as an immutable snapshot on issue). */
export const partySnapshotSchema = z.object({
    legalName: z.string(),
    customerCode: z.string().nullish(),
    taxIdentifier: z.string().nullish(),
    taxIdentifierType: taxIdentifierTypeSchema.nullish(),
    email: z.string().nullish(),
    phone: z.string().nullish(),
    streetName: z.string().nullish(),
    cityName: z.string().nullish(),
    stateCode: z.string().nullish(),
    lgaCode: z.string().nullish(),
    postalZone: z.string().nullish(),
    countryCode: z.string().nullish(),
});
/** One-off buyer typed directly into an invoice. */
export const buyerInputSchema = z.object({
    legalName: z.string().trim().min(1, 'Enter the buyer name').max(200),
    taxIdentifier: optionalText(40),
    email: optionalEmailSchema,
    phone: optionalText(30),
    ...addressFieldsShape,
});
// ───────────── customers ─────────────
export const customerInputSchema = z.object({
    taxpayerProfileId: z.uuid(),
    customerCode: z
        .string()
        .trim()
        .max(40)
        .nullish()
        .transform((v) => (v === undefined ? undefined : v ? v : null)),
    legalName: z.string().trim().min(1, 'Enter the customer name').max(200),
    taxIdentifier: optionalText(40),
    email: optionalEmailSchema,
    phone: optionalText(30),
    ...addressFieldsShape,
});
export const customerUpdateSchema = patchOf(customerInputSchema.omit({ taxpayerProfileId: true }));
export const customerListQuerySchema = paginationQuerySchema.extend({
    taxpayerProfileId: z.uuid().optional(),
    archived: z.stringbool().optional(),
});
export const customerSchema = z.object({
    id: z.uuid(),
    taxpayerProfileId: z.uuid(),
    customerCode: z.string().nullable(),
    legalName: z.string(),
    taxIdentifier: z.string().nullable(),
    email: z.string().nullable(),
    phone: z.string().nullable(),
    streetName: z.string().nullable(),
    cityName: z.string().nullable(),
    stateCode: z.string().nullable(),
    lgaCode: z.string().nullable(),
    postalZone: z.string().nullable(),
    countryCode: z.string(),
    archivedAt: z.string().nullable(),
    createdAt: z.string(),
    stats: z
        .object({
        invoiceCount: z.number(),
        invoicedTotal: z.string(),
        outstandingTotal: z.string(),
    })
        .optional(),
});
// ───────────── products / services ─────────────
export const productInputSchema = z.object({
    taxpayerProfileId: z.uuid(),
    itemCode: z
        .string()
        .trim()
        .min(1, 'Enter an item code')
        .max(60)
        .regex(/^[A-Za-z0-9._\-/]+$/, 'Letters, numbers and . _ - / only'),
    name: z.string().trim().min(1, 'Enter a name').max(200),
    description: optionalText(2000),
    lineType: lineTypeSchema,
    priceUnit: z.string().trim().min(1).max(20).default('EA'),
    unitPrice: decimalInput({ min: 0 }),
    currencyCode: currencyCodeSchema.default('NGN'),
    defaultTaxCategoryId: z.uuid().nullish(),
    isActive: z.boolean().default(true),
});
export const productUpdateSchema = patchOf(productInputSchema.omit({ taxpayerProfileId: true }));
export const productListQuerySchema = paginationQuerySchema.extend({
    taxpayerProfileId: z.uuid().optional(),
    lineType: lineTypeSchema.optional(),
    active: z.stringbool().optional(),
});
export const productSchema = z.object({
    id: z.uuid(),
    taxpayerProfileId: z.uuid(),
    itemCode: z.string(),
    name: z.string(),
    description: z.string().nullable(),
    lineType: lineTypeSchema,
    priceUnit: z.string(),
    unitPrice: z.string(),
    currencyCode: z.string(),
    defaultTaxCategoryId: z.uuid().nullable(),
    isActive: z.boolean(),
    createdAt: z.string(),
});
export const taxCategorySchema = z.object({
    id: z.uuid(),
    code: z.string(),
    name: z.string(),
    description: z.string().nullable(),
    defaultPercent: z.string(),
    isActive: z.boolean(),
});
export { countryCodeSchema };
//# sourceMappingURL=parties.js.map